<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import api from '@/api';
import { PRODUCT_NAME } from '@/constants';
import { DgrIcon } from '@stockmarkteam/donguri-ui';
import { useSnackbar } from '@/components/common/snackbar/use-snackbar';
import Header from '@/components/layouts/header.vue';
import { STATES, useAdminSamlFederation } from '@/utils/swr';
import { userSession } from '@/utils/userSession';

const route = useRoute();
const router = useRouter();
const { createSnackbar } = useSnackbar();

const name = ref('');
const hasNameError = computed(() => name.value.length > 20);
const metadataUrl = ref('');
const metadataUrlError = ref('');
const idpEntityId = ref('');
const idpSsoServiceUrl = ref('');

const status = ref<'未完了' | '完了' | 'エラー'>('未完了');
const operationCheckMetadataUrl = ref('');

const { data: samlFederation, state } = useAdminSamlFederation();

const isLoaded = computed(() => state.value === STATES.SUCCESS);

const setIdpDataFromSamlFederation = () => {
  if (idpEntityId.value === '') {
    idpEntityId.value = samlFederation.value?.idp_entity_id ?? '';
  }
  if (idpSsoServiceUrl.value === '') {
    idpSsoServiceUrl.value = samlFederation.value?.idp_sso_service_url ?? '';
  }
};

const setDataFromSamlFederation = () => {
  if (!samlFederation.value) {
    return;
  }
  if (name.value === '') {
    name.value = samlFederation.value?.name ?? '';
  }
  if (metadataUrl.value === '') {
    metadataUrl.value = samlFederation.value?.federation_metadata_url ?? '';
    if ((samlFederation.value?.federation_metadata_url ?? '').length > 0) {
      status.value = '完了';
    }
  }
  setIdpDataFromSamlFederation();
};

watch(samlFederation, () => {
  setDataFromSamlFederation();
});

onMounted(() => {
  if (route.name === 'samlFederationCallback') {
    const operationCheckId = route.query.id;
    const {
      id: currentId,
      name: currentName,
      metadataUrl: currentMetadataUrl,
      idpEntityId: currentIdpEntityId,
      idpSsoServiceUrl: currentIdpSsoServiceUrl,
    } = userSession.getSamlFederationAdminOperationCheckData();
    if (operationCheckId === currentId && currentMetadataUrl) {
      operationCheckMetadataUrl.value = currentMetadataUrl;
      name.value = currentName;
      metadataUrl.value = currentMetadataUrl;
      idpEntityId.value = currentIdpEntityId;
      idpSsoServiceUrl.value = currentIdpSsoServiceUrl;
    } else {
      status.value = 'エラー';
    }
  } else {
    userSession.clearSamlFederationAdminOperationCheckData();
  }
  setDataFromSamlFederation();
});

const copyText = async (text: string) => {
  try {
    await navigator.clipboard.writeText(text);
    createSnackbar({
      message: 'リンクをコピーしました',
      type: 'success',
    });
  } catch (_e) {
    createSnackbar({
      message: 'リンクをコピーできませんでした',
      type: 'error',
    });
  }
};

const setOperationCheckError = () => {
  metadataUrlError.value =
    '入力中のURLに不備があります。ご確認の上、再度入力してください。';
  status.value = 'エラー';
};

const testSamlFederation = async () => {
  if (metadataUrlError.value !== '') {
    status.value = 'エラー';
    return;
  }
  if (metadataUrl.value === '') {
    metadataUrlError.value = 'URLを入力してください。';
    status.value = 'エラー';
    return;
  }
  userSession.clearSamlFederationAdminOperationCheckData();
  try {
    const {
      login_url: loginUrl,
      idp_entity_id: idpEntityId,
      idp_sso_service_url: idpSsoServiceUrl,
      operation_check_id: operationCheckId,
    } = await api.getSamlFederationLoginUrl(metadataUrl.value);
    if (loginUrl) {
      userSession.setSamlFederationAdminOperationCheckData(
        operationCheckId,
        name.value,
        metadataUrl.value,
        idpEntityId ?? '',
        idpSsoServiceUrl ?? '',
      );
      document.location.href = loginUrl;
    } else {
      setOperationCheckError();
    }
  } catch (_e) {
    setOperationCheckError();
  }
};

const isValidHttpsUrl = (string: string) => {
  let url;

  try {
    url = new URL(string);
  } catch (_) {
    return false;
  }

  return url.protocol === 'https:';
};

watch(metadataUrl, newMetadataUrl => {
  metadataUrlError.value = '';
  if (
    newMetadataUrl.length > 0 &&
    (newMetadataUrl === operationCheckMetadataUrl.value ||
      newMetadataUrl === samlFederation.value?.federation_metadata_url)
  ) {
    if (newMetadataUrl === samlFederation.value?.federation_metadata_url) {
      setIdpDataFromSamlFederation();
    }
    status.value = '完了';
    return;
  }

  status.value = '未完了';
  if (newMetadataUrl === '') {
    metadataUrlError.value = 'URLを入力してください。';
    return;
  }
  if (!isValidHttpsUrl(newMetadataUrl)) {
    metadataUrlError.value = '不正なURLが入力されています。';
    return;
  }
});

const isSaveEnabled = computed(() => {
  return (
    status.value === '完了' &&
    !hasNameError.value &&
    metadataUrl.value.length > 0 &&
    metadataUrlError.value === ''
  );
});

const save = async () => {
  if (!isSaveEnabled.value) {
    return;
  }
  const isUpdate =
    !!samlFederation.value?.name ||
    !!samlFederation.value?.federation_metadata_url;
  try {
    await api.updateSamlFederation({
      name: name.value,
      metadataUrl: metadataUrl.value,
    });
    router.push({ name: 'samlFederationList' });
    createSnackbar({
      message: isUpdate
        ? '登録中のIdP設定を更新しました'
        : '設定を保存しました',
      type: 'success',
    });
  } catch (error) {
    createSnackbar({
      message: '設定の保存に失敗しました',
      type: 'error',
    });
    throw error;
  }
};

const goBack = () => {
  router.push({ name: 'samlFederationList' });
};
</script>

<template>
  <div class="saml-federation-setting">
    <Header title="SSO認証" header-width="min(912px, 100%)" />
    <div class="body" v-if="isLoaded">
      <div class="sso-card idp-info">
        <span class="c-text-regular--s help-text"
          >ヘルプを参照しながらの設定を推奨しています。<a
            href="https://anews-stockmark.zendesk.com/hc/ja/articles/43162727635993"
            target="_blank"
            rel="noopener noreferrer"
            class="help-link"
            >SSO認証を設定する<DgrIcon
              size="small"
              name="external-link"
              class="help-link-icon"
              :keep-fill="false" /></a
        ></span>
        <span class="c-title-bold--s">IdP設定</span>
        <span class="c-title-bold--m">IdPに設定する情報</span>

        <span class="label c-text-regular--s">識別子（Entity ID）</span>
        <span class="c-textInput disabled c-text-regular--m">
          {{ samlFederation?.entity_id }}
          <DgrIcon
            size="small"
            name="copy"
            class="copy-icon"
            :keep-fill="false"
            @click="copyText(samlFederation?.entity_id ?? '')"
          />
        </span>

        <span class="label c-text-regular--s"
          >応答URL（Assertion Consumer Service URL)</span
        >
        <span class="c-textInput disabled c-text-regular--m">
          {{ samlFederation?.acs_url }}
          <DgrIcon
            size="small"
            name="copy"
            class="copy-icon"
            :keep-fill="false"
            @click="copyText(samlFederation?.acs_url ?? '')"
          />
        </span>
      </div>

      <div class="sso-card anews-info">
        <span class="c-title-bold--s">{{ PRODUCT_NAME }}設定</span>

        <div class="name-container">
          <span class="c-title-bold--m">設定名</span>
          <input
            type="text"
            v-model.trim="name"
            class="c-textInput"
            :class="{ 'c-formInput--error': name !== '' && hasNameError }"
            placeholder="会社用IdP設定"
          />
          <span class="name-subtitle c-text-regular--s">
            <span v-if="hasNameError" class="error-text"
              >20文字以内で入力してください。</span
            >
            <span class="count" :class="{ 'error-text': hasNameError }"
              >{{ name.length }}/20</span
            >
          </span>
        </div>

        <div class="spacing-24"></div>

        <span class="c-title-bold--m"
          >アプリのフェデレーションメタデータURL（App Federation Metadata
          URL）</span
        >
        <input
          type="text"
          v-model.trim="metadataUrl"
          class="c-textInput"
          :class="{ 'c-formInput--error': metadataUrlError !== '' }"
          placeholder="例：https://login.microsoftonline.com/common/FederationMetadata/2007-06/FederationMetadata.xml"
        />
        <span class="error-text c-text-regular--s">{{ metadataUrlError }}</span>

        <div class="spacing-24"></div>

        <span class="c-title-bold--m">動作テスト</span>
        <span class="label c-text-regular--s info-text"
          >{{
            PRODUCT_NAME
          }}とIdPの連携を検証します。実行するとIdPのログイン画面へ移動します。</span
        >
        <div class="test-container">
          <button class="c-btn c-btn--small" @click="testSamlFederation">
            テストを実行
          </button>
          <span
            class="c-text-regular--s"
            :class="{
              untested: status === '未完了',
              success: status === '完了',
              error: status === 'エラー',
            }"
          >
            <DgrIcon
              v-if="status !== '未完了'"
              size="small"
              :name="status === '完了' ? 'check' : 'times'"
              :keep-fill="false"
              class="status-icon"
              :class="{
                success: status === '完了',
                error: status === 'エラー',
              }"
            />
            {{ status }}
          </span>
        </div>
      </div>

      <div class="sso-card idp-info">
        <span class="c-title-bold--s">IdP詳細</span>

        <span class="c-title-bold--m">連携中のIdP情報</span>

        <span class="label c-text-regular--s">Issuer</span>
        <span class="c-textInput disabled c-text-regular--m">
          {{ idpEntityId.length > 0 ? idpEntityId : '連携中のIdPのIssuer' }}
        </span>

        <span class="label c-text-regular--s">SSO Service URL</span>
        <span class="c-textInput disabled c-text-regular--m">
          {{
            idpSsoServiceUrl.length > 0
              ? idpSsoServiceUrl
              : '連携中のIdPのSSO Service URL'
          }}
        </span>
      </div>
    </div>
    <div class="action-buttons" v-if="isLoaded">
      <div class="action-buttons-inner">
        <button
          class="c-btn c-btn--small c-btn--primary"
          @click="save"
          :class="{
            disabled: !isSaveEnabled,
          }"
        >
          保存
        </button>
        <button class="c-btn c-btn--small back-button" @click="goBack">
          キャンセル
        </button>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.saml-federation-setting {
  display: flex;
  flex-direction: column;
  width: 100%;
  min-height: 100%;
  .body {
    display: flex;
    flex-direction: column;
    gap: 16px;
    box-sizing: border-box;
    width: 100%;
    max-width: calc(912px + 48px);
    margin: 0 auto;
    padding: 24px;

    .sso-card {
      display: flex;
      flex-direction: column;

      box-sizing: border-box;
      background-color: #fff;
      border: 1px solid $color-gray400;
      border-radius: 4px;
      padding: 16px;
    }

    .help-text {
      margin-bottom: 24px;

      .help-link {
        color: $color-green600;
      }
      .help-link,
      .help-link-icon {
        display: inline-flex;
        color: $color-green600;
      }
      .help-link-icon {
        margin-left: 4px;
      }
    }
    .c-text-regular--s,
    .c-text-regular--m {
      color: $color-gray1000;
    }
    .c-title-bold--s,
    .info-text {
      color: $color-gray600;
    }
    .error-text {
      color: $color-orange1000;
    }
    .c-title-bold--s {
      margin-bottom: 8px;
    }
    .label {
      margin: 4px 0;
    }
    .c-textInput {
      max-width: 640px;
      margin-bottom: 4px;

      &.disabled {
        display: flex;
        justify-content: space-between;
        align-items: center;
        background-color: $color-gray200;
        min-height: 24px;
        height: auto;
        border-radius: 4px;
        color: $color-gray800;

        .icon-box {
          flex-shrink: 0;
        }
      }
    }
    .c-btn--small {
      width: fit-content;
      padding: 4px 16px;
    }

    .copy-icon {
      cursor: pointer;
      color: $color-green600;
    }

    .name-container {
      width: 240px;

      .name-subtitle {
        display: flex;
        justify-content: flex-end;

        .count {
          margin-left: 8px;
        }
      }
    }

    .test-container {
      display: flex;
      align-items: center;
      gap: 8px;

      & > span {
        display: flex;
        align-items: center;
        gap: 4px;
      }
      .untested {
        color: $color-gray600;
      }
      .success {
        color: $color-green600;
      }
      .error {
        color: $color-orange1000;
      }
    }

    .idp-name {
      margin-left: 8px;
      color: $color-green600;
    }
  }

  // 内容が短くても画面の最下部に置く
  .action-buttons {
    position: sticky;
    bottom: 0;
    margin-top: auto;
    padding: 16px 24px;
    background: $color-white;
    box-shadow: 0px 1px 5px rgba($color-gray1000, 0.25);

    .action-buttons-inner {
      display: flex;
      gap: 8px;
      max-width: 912px;
      margin: 0 auto;
    }

    .c-btn--small {
      width: fit-content;
      padding: 4px 16px;
    }

    .back-button {
      background-color: #fff;

      &:hover {
        background-color: $color-gray400;
      }
    }
  }
}
</style>
